import React from "react";
import { FaWhatsapp } from "react-icons/fa";
import { Activity, Droplet, Stethoscope, Syringe, FlaskConical, Pill, BadgePercent, PackageSearch } from "lucide-react";
import { useLang } from "../lib/LangContext";
import { waLink } from "../lib/mock";

const ICONS = [Activity, Droplet, Stethoscope, Syringe, FlaskConical, Pill];

export const Catalog = () => {
  const { t } = useLang();
  const c = t.catalog;
  return (
    <section className="px-6 md:px-12 lg:px-20 max-w-[1400px] mx-auto pt-2 pb-6 md:pt-4 md:pb-10">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Offer card — tangerine spotlight */}
        <div
          data-testid="catalog-offer-card"
          className="lg:col-span-5 relative overflow-hidden rounded-3xl bg-lokam-tangerine text-white p-8 md:p-10 flex flex-col"
        >
          <div
            aria-hidden
            className="absolute -top-20 -right-20 h-64 w-64 rounded-full bg-white/15 blur-3xl"
          />
          <div
            aria-hidden
            className="absolute -bottom-24 -left-16 h-72 w-72 rounded-full bg-black/10 blur-3xl"
          />

          <div className="relative">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/15 backdrop-blur-md border border-white/25 px-3 py-1.5 text-[11px] uppercase tracking-[0.2em] font-bold">
              <BadgePercent className="h-3.5 w-3.5" />
              {c.offer_kicker}
            </div>

            <h3 className="mt-6 font-display text-3xl md:text-[40px] leading-[1.05] font-bold tracking-tight text-balance">
              {c.offer_title}
            </h3>

            <p className="mt-5 text-white/90 leading-relaxed text-sm md:text-base max-w-md">
              {c.offer_body}
            </p>

            <a
              href={waLink()}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="catalog-offer-cta"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-white text-lokam-tangerine hover:bg-lokam-forest hover:text-lokam-cream transition-colors px-5 py-3.5 text-sm font-semibold w-fit"
            >
              <FaWhatsapp className="h-5 w-5" />
              {c.offer_cta}
            </a>
          </div>

          {/* Big watermark number */}
          <div
            aria-hidden
            className="absolute right-6 bottom-2 font-display font-black text-[140px] md:text-[180px] leading-none text-white/10 select-none pointer-events-none"
          >
            20%
          </div>
        </div>

        {/* Catalogue depth — forest dark card */}
        <div
          data-testid="catalog-depth-card"
          className="lg:col-span-7 relative overflow-hidden rounded-3xl bg-lokam-forest text-lokam-cream p-8 md:p-10"
        >
          <div
            aria-hidden
            className="absolute inset-0 opacity-[0.06] pointer-events-none"
            style={{
              backgroundImage:
                "radial-gradient(circle at 1px 1px, #FFFDF9 1px, transparent 0)",
              backgroundSize: "28px 28px",
            }}
          />

          <div className="relative">
            <div className="inline-flex items-center gap-2 rounded-full bg-lokam-cream/10 backdrop-blur-md border border-white/15 px-3 py-1.5 text-[11px] uppercase tracking-[0.2em] font-bold text-lokam-tangerine">
              <PackageSearch className="h-3.5 w-3.5" />
              {c.catalog_kicker}
            </div>

            <div className="mt-6 flex flex-wrap items-end gap-x-4 gap-y-1">
              <span className="font-display font-black text-5xl md:text-7xl leading-none text-lokam-tangerine">
                30,000+
              </span>
              <span className="font-display font-semibold text-xl md:text-2xl text-lokam-cream/85">
                medicines & healthcare products
              </span>
            </div>

            <p className="mt-5 text-lokam-cream/75 leading-relaxed text-sm md:text-base max-w-2xl">
              {c.catalog_body}
            </p>

            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {c.cats.map((cat, i) => {
                const Icon = ICONS[i % ICONS.length];
                return (
                  <div
                    key={i}
                    data-testid={`catalog-cat-${i}`}
                    className="group rounded-xl border border-white/10 bg-lokam-forest-soft/40 hover:bg-lokam-forest-soft/70 transition-colors p-4 flex items-start gap-3"
                  >
                    <div className="h-9 w-9 rounded-lg bg-lokam-tangerine/15 text-lokam-tangerine flex items-center justify-center flex-shrink-0">
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-display font-semibold text-base text-lokam-cream leading-tight">
                        {cat.title}
                      </div>
                      <div className="text-xs text-lokam-cream/65 mt-1 leading-snug">
                        {cat.note}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Catalog;
